import { BULLET_SPEED, GROUND_HEIGHT, POWER_UP_TYPES, DEFAULT_GROUND_Y, WALKABLE_DEPTH, getDepthScale } from './constants';

export class Bullet {
  constructor(x, y, direction, isEnemy = false, isGrenade = false, isRocket = false, isTankShell = false, isRapidFire = false, isAssaultRifle = false) {
    this.x = x;
    this.y = y;
    this.direction = direction;
    this.isEnemy = isEnemy;
    this.isGrenade = isGrenade;
    this.isRocket = isRocket;
    this.isTankShell = isTankShell;
    this.isRapidFire = isRapidFire;
    this.isAssaultRifle = isAssaultRifle;
    this.active = true;
    // 2.5D depth - set by spawner to match shooter's depth
    this.groundY = DEFAULT_GROUND_Y;
    
    this.isLaser = false;
    this.isExplosive = false;
    this.isSpread = false;
    this.doubleDamage = false;
    this.pierceCount = 0;
    
    if (isGrenade) {
      this.width = 10; this.height = 10;
      this.velocityX = direction * 8; this.velocityY = -8; this.damage = 30;
    } else if (isRocket) {
      this.width = 20; this.height = 8;
      this.velocityX = direction * 10; this.velocityY = 0; this.damage = 50; this.blastRadius = 80;
    } else if (isTankShell) {
      this.width = 15; this.height = 6;
      this.velocityX = direction * 14; this.velocityY = 0; this.damage = 35;
    } else if (isAssaultRifle) {
      // Assault Rifle: faster, longer, more damage than default; neon-green tracer
      this.width = 16; this.height = 3;
      this.velocityX = direction * (BULLET_SPEED * 1.4); this.velocityY = 0; this.damage = 18;
    } else {
      this.width = 12; this.height = 4;
      this.velocityX = direction * BULLET_SPEED; this.velocityY = 0; this.damage = 10;
    }
  }

  getDamage() {
    let dmg = this.damage;
    if (this.doubleDamage) dmg *= 2;
    if (this.isLaser) dmg *= 1.5;
    return dmg;
  }

  update(canvasHeight) {
    this.x += this.velocityX;
    
    if (this.isGrenade) {
      this.velocityY += 0.3;
      this.y += this.velocityY;
      // Grenade explodes when it hits the ground at its depth position
      const groundLevel = canvasHeight - GROUND_HEIGHT + 15 + this.groundY;
      if (this.y > groundLevel - 10) {
        this.active = false;
        return true;
      }
    }
    return false;
  }

  draw(ctx, cameraX) {
    const screenX = this.x - cameraX;
    
    if (this.isGrenade) {
      ctx.fillStyle = '#FF9500';
      ctx.beginPath();
      ctx.arc(screenX + 5, this.y + 5, 5, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = 'rgba(255, 149, 0, 0.5)';
      ctx.beginPath();
      ctx.arc(screenX + 5 - this.velocityX, this.y + 5 - this.velocityY, 3, 0, Math.PI * 2);
      ctx.fill();
    } else if (this.isRocket) {
      ctx.save();
      ctx.fillStyle = '#FF4444';
      ctx.fillRect(screenX, this.y, this.width, this.height);
      ctx.beginPath();
      ctx.moveTo(screenX + (this.direction === 1 ? this.width : 0), this.y);
      ctx.lineTo(screenX + (this.direction === 1 ? this.width + 8 : -8), this.y + this.height / 2);
      ctx.lineTo(screenX + (this.direction === 1 ? this.width : 0), this.y + this.height);
      ctx.fillStyle = '#FF6666';
      ctx.fill();
      ctx.fillStyle = '#FF9500';
      for (let i = 0; i < 3; i++) {
        const trailX = screenX - this.direction * (10 + i * 8);
        ctx.globalAlpha = 0.7 - i * 0.2;
        ctx.beginPath();
        ctx.arc(trailX, this.y + this.height / 2, 4 - i, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
      ctx.restore();
    } else if (this.isTankShell) {
      ctx.fillStyle = '#4A90D9';
      ctx.fillRect(screenX, this.y, this.width, this.height);
      ctx.fillStyle = 'rgba(74, 144, 217, 0.4)';
      ctx.fillRect(screenX - Math.abs(this.velocityX), this.y - 2, Math.abs(this.velocityX) * 2, this.height + 4);
    } else {
      if (this.isRapidFire && !this.isEnemy) {
        ctx.fillStyle = '#FF3B30';
        ctx.fillRect(screenX, this.y, this.width, this.height);
        ctx.fillStyle = 'rgba(255, 59, 48, 0.6)';
        ctx.fillRect(screenX - this.velocityX * 3, this.y - 1, Math.abs(this.velocityX * 3), this.height + 2);
        ctx.shadowColor = '#FF3B30';
        ctx.shadowBlur = 8;
        ctx.fillStyle = '#FF6B6B';
        ctx.fillRect(screenX + 2, this.y + 1, this.width - 4, this.height - 2);
        ctx.shadowBlur = 0;
      } else if (this.isAssaultRifle && !this.isEnemy) {
        // Neon-green tracer with crisp core + long fading trail
        ctx.save();
        ctx.shadowColor = '#14F195';
        ctx.shadowBlur = 10;
        // Long trail
        const trailLen = Math.abs(this.velocityX * 2.5);
        ctx.fillStyle = 'rgba(20, 241, 149, 0.45)';
        ctx.fillRect(screenX - this.direction * trailLen, this.y, trailLen, this.height);
        // Bright outer
        ctx.fillStyle = '#14F195';
        ctx.fillRect(screenX, this.y, this.width, this.height);
        // White-hot inner
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(screenX + 3, this.y + 1, this.width - 6, this.height - 1);
        ctx.shadowBlur = 0;
        ctx.restore();
      } else if (this.isLaser && !this.isEnemy) {
        ctx.save();
        ctx.shadowColor = '#00FF00';
        ctx.shadowBlur = 15;
        ctx.fillStyle = '#00FF00';
        ctx.fillRect(screenX, this.y - 1, this.width + 8, this.height + 2);
        ctx.fillStyle = '#AAFFAA';
        ctx.fillRect(screenX, this.y, this.width + 8, this.height);
        ctx.fillStyle = 'rgba(0, 255, 0, 0.4)';
        ctx.fillRect(screenX - Math.abs(this.velocityX * 5), this.y - 2, Math.abs(this.velocityX * 5), this.height + 4);
        ctx.shadowBlur = 0;
        ctx.restore();
      } else if (this.isExplosive && !this.isEnemy) {
        ctx.save();
        ctx.shadowColor = '#FF4500';
        ctx.shadowBlur = 12;
        ctx.fillStyle = '#FF4500';
        ctx.fillRect(screenX, this.y, this.width, this.height);
        const flicker = Math.random() * 0.3;
        ctx.globalAlpha = 0.7 + flicker;
        ctx.fillStyle = '#FFAA00';
        ctx.fillRect(screenX + 2, this.y + 1, this.width - 4, this.height - 2);
        ctx.fillStyle = 'rgba(255, 165, 0, 0.5)';
        for (let i = 0; i < 3; i++) {
          const sparkX = screenX - this.velocityX * (1 + i) + (Math.random() - 0.5) * 4;
          const sparkY = this.y + (Math.random() - 0.5) * 6;
          ctx.beginPath();
          ctx.arc(sparkX, sparkY, 2 - i * 0.5, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.globalAlpha = 1;
        ctx.shadowBlur = 0;
        ctx.restore();
      } else if (this.isSpread && !this.isEnemy) {
        ctx.save();
        ctx.shadowColor = '#FF00FF';
        ctx.shadowBlur = 8;
        ctx.fillStyle = '#FF00FF';
        ctx.fillRect(screenX, this.y, this.width, this.height);
        ctx.fillStyle = 'rgba(255, 0, 255, 0.4)';
        ctx.fillRect(screenX - this.velocityX * 2, this.y - 1, Math.abs(this.velocityX * 2), this.height + 2);
        ctx.shadowBlur = 0;
        ctx.restore();
      } else if (this.doubleDamage && !this.isEnemy && !this.isRapidFire) {
        ctx.save();
        ctx.shadowColor = '#FF1493';
        ctx.shadowBlur = 10;
        ctx.fillStyle = '#FF1493';
        ctx.fillRect(screenX, this.y, this.width, this.height);
        const pulse = Math.sin(Date.now() * 0.02) * 0.3 + 0.7;
        ctx.globalAlpha = pulse;
        ctx.fillStyle = '#FFB6C1';
        ctx.fillRect(screenX + 2, this.y + 1, this.width - 4, this.height - 2);
        ctx.globalAlpha = 1;
        ctx.shadowBlur = 0;
        ctx.restore();
      } else {
        ctx.fillStyle = this.isEnemy ? '#FF3B30' : '#14F195';
        ctx.fillRect(screenX, this.y, this.width, this.height);
        ctx.fillStyle = this.isEnemy ? 'rgba(255, 59, 48, 0.3)' : 'rgba(20, 241, 149, 0.3)';
        ctx.fillRect(screenX - this.velocityX * 2, this.y - 1, Math.abs(this.velocityX * 2), this.height + 2);
      }
    }
  }
}

// Monotonic power-up id counter for host-authoritative multiplayer sync
let _powerUpIdCounter = 1;

export class PowerUp {
  constructor(x, y, type) {
    this.id = `p${_powerUpIdCounter++}`;
    this.x = x;
    this.y = y;
    this.width = 30;
    this.height = 30;
    this.type = type;
    this.active = true;
    this.bobOffset = Math.random() * Math.PI * 2;
    // 2.5D depth - random spawn depth
    this.groundY = Math.random() * WALKABLE_DEPTH;
  }

  update() {
    this.bobOffset += 0.1;
  }

  draw(ctx, cameraX, canvasHeight) {
    const screenX = this.x - cameraX;
    const feetY = canvasHeight - GROUND_HEIGHT + 15 + this.groundY;
    const baseY = feetY - this.height;
    const bobY = baseY + Math.sin(this.bobOffset) * 5;
    
    // Depth-based perspective scaling
    const scale = getDepthScale(this.groundY);
    
    ctx.save();
    const cx = screenX + 15;
    const cy = bobY + 15;
    ctx.translate(cx, cy);
    ctx.scale(scale, scale);
    ctx.translate(-cx, -cy);
    
    ctx.shadowColor = this.getColor();
    ctx.shadowBlur = 15;
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.beginPath();
    ctx.arc(screenX + 15, bobY + 15, 18, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = this.getColor();
    ctx.font = 'bold 16px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(this.getIcon(), screenX + 15, bobY + 15);
    ctx.restore();
  }

  getColor() {
    switch (this.type) {
      case POWER_UP_TYPES.RAPID_FIRE: return '#14F195';
      case POWER_UP_TYPES.SHIELD: return '#5AC8FA';
      case POWER_UP_TYPES.HEALTH: return '#FF3B30';
      case POWER_UP_TYPES.GRENADE: return '#FF9500';
      case POWER_UP_TYPES.SPEED: return '#FFCC00';
      case POWER_UP_TYPES.SPREAD_SHOT: return '#FF00FF';
      case POWER_UP_TYPES.DOUBLE_DAMAGE: return '#FF1493';
      case POWER_UP_TYPES.FREEZE: return '#00FFFF';
      case POWER_UP_TYPES.MAGNET: return '#9400D3';
      case POWER_UP_TYPES.LASER: return '#00FF00';
      case POWER_UP_TYPES.EXPLOSIVE_ROUNDS: return '#FF4500';
      case POWER_UP_TYPES.EXTRA_LIFE: return '#FF1493';
      default: return '#fff';
    }
  }

  getIcon() {
    switch (this.type) {
      case POWER_UP_TYPES.RAPID_FIRE: return 'R';
      case POWER_UP_TYPES.SHIELD: return 'S';
      case POWER_UP_TYPES.HEALTH: return '+';
      case POWER_UP_TYPES.GRENADE: return 'G';
      case POWER_UP_TYPES.SPEED: return '»';
      case POWER_UP_TYPES.SPREAD_SHOT: return '⋮';
      case POWER_UP_TYPES.DOUBLE_DAMAGE: return '2x';
      case POWER_UP_TYPES.FREEZE: return '❄';
      case POWER_UP_TYPES.MAGNET: return 'M';
      case POWER_UP_TYPES.LASER: return '—';
      case POWER_UP_TYPES.EXPLOSIVE_ROUNDS: return '💥';
      case POWER_UP_TYPES.EXTRA_LIFE: return '♥';
      default: return '?';
    }
  }
}

export class Particle {
  constructor(x, y, color, size = null, velocityX = null, velocityY = null) {
    this.x = x;
    this.y = y;
    this.size = size || Math.random() * 6 + 2;
    this.velocityX = velocityX || (Math.random() - 0.5) * 8;
    this.velocityY = velocityY || (Math.random() - 0.5) * 8 - 3;
    this.color = color;
    this.life = 1;
    this.decay = Math.random() * 0.03 + 0.02;
  }

  update() {
    this.x += this.velocityX;
    this.y += this.velocityY;
    this.velocityY += 0.2;
    this.life -= this.decay;
  }

  draw(ctx, cameraX) {
    if (this.life <= 0) return;
    ctx.globalAlpha = this.life;
    if (this.isHeart) {
      // Render a glowing pink heart
      ctx.save();
      ctx.shadowColor = '#FF1493';
      ctx.shadowBlur = 12;
      ctx.fillStyle = this.color;
      ctx.font = `bold ${Math.round(this.size * 2.5)}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('♥', this.x - cameraX, this.y);
      ctx.shadowBlur = 0;
      ctx.restore();
    } else {
      ctx.fillStyle = this.color;
      ctx.fillRect(this.x - cameraX, this.y, this.size, this.size);
    }
    ctx.globalAlpha = 1;
  }
}

// Brass-casing particle ejected from Assault Rifle on each shot.
// Spins and falls under gravity, then settles on the ground with a glint fade-out.
export class BrassCasing {
  constructor(x, y, direction) {
    this.x = x;
    this.y = y;
    // Eject opposite the firing direction with a small upward kick
    this.velocityX = -direction * (1.5 + Math.random() * 1.5);
    this.velocityY = -3 - Math.random() * 1.5;
    this.angle = Math.random() * Math.PI * 2;
    this.spin = (Math.random() - 0.5) * 0.6;
    this.life = 1;
    this.decay = 0.012;
    this.width = 4;
    this.height = 2;
    this.grounded = false;
    this.groundOffsetY = 0;
  }

  update(canvasHeight) {
    if (this.grounded) {
      this.life -= this.decay;
      return;
    }
    this.x += this.velocityX;
    this.y += this.velocityY;
    this.velocityY += 0.3; // gravity
    this.angle += this.spin;
    // Settle when hitting the ground band
    const groundLevel = canvasHeight - GROUND_HEIGHT + 20;
    if (this.y >= groundLevel) {
      this.y = groundLevel;
      this.velocityX *= 0.4; // bounce friction
      this.velocityY *= -0.25; // tiny bounce
      if (Math.abs(this.velocityY) < 0.5) {
        this.grounded = true;
        this.spin = 0;
      }
    }
  }

  draw(ctx, cameraX) {
    if (this.life <= 0) return;
    const screenX = this.x - cameraX;
    ctx.save();
    ctx.globalAlpha = this.life;
    ctx.translate(screenX + this.width / 2, this.y + this.height / 2);
    ctx.rotate(this.angle);
    // Casing body (brass)
    ctx.fillStyle = '#D4A017';
    ctx.fillRect(-this.width / 2, -this.height / 2, this.width, this.height);
    // Highlight stripe
    ctx.fillStyle = '#FFE082';
    ctx.fillRect(-this.width / 2, -this.height / 2, this.width, 1);
    // Rim
    ctx.fillStyle = '#8B6914';
    ctx.fillRect(-this.width / 2, -this.height / 2, 1, this.height);
    ctx.restore();
    ctx.globalAlpha = 1;
  }
}


// Floating "+50" combat text that rises and fades over ~1s.
// Spawned above a player when they score a kill (local or remote shooter).
export class FloatingText {
  constructor(x, y, text, color = '#FFD700') {
    this.x = x;
    this.y = y;
    this.text = text;
    this.color = color;
    this.life = 1;
    this.decay = 0.018;       // ~55 frames @ 60fps
    this.driftY = -1.4;       // pixels per frame upward
    this.driftX = (Math.random() - 0.5) * 0.5;
    this.scale = 1;
  }

  update() {
    this.y += this.driftY;
    this.x += this.driftX;
    this.driftY *= 0.97;      // ease out
    this.life -= this.decay;
    // Brief pop-in scale during first 8 frames
    if (this.scale < 1.15 && this.life > 0.85) this.scale += 0.04;
    else if (this.scale > 1) this.scale -= 0.02;
  }

  draw(ctx, cameraX) {
    if (this.life <= 0) return;
    const screenX = this.x - cameraX;
    const alpha = Math.min(1, this.life * 1.5);
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.translate(screenX, this.y);
    ctx.scale(this.scale, this.scale);
    ctx.font = 'bold 16px "Press Start 2P", monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    // Black outline for legibility on busy backgrounds
    ctx.lineWidth = 4;
    ctx.strokeStyle = '#000';
    ctx.strokeText(this.text, 0, 0);
    // Glow fill
    ctx.shadowColor = this.color;
    ctx.shadowBlur = 8;
    ctx.fillStyle = this.color;
    ctx.fillText(this.text, 0, 0);
    ctx.restore();
  }
}
